import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ChatBotErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ChatBot Error:', error);
    console.error('Error Info:', errorInfo);
    
    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    
    // Log to analytics/monitoring service
    this.logError(error, errorInfo);
  }

  private logError = (error: Error, errorInfo: ErrorInfo) => {
    // Integration point for error logging service
    try {
      const errorData = {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        userId: sessionStorage.getItem('currentUserId') || 'unknown'
      };
      
      console.error('ChatBot Error Data:', errorData);
      
      // TODO: Send to error logging service
      // errorLoggingService.logError(errorData);
      
    } catch (loggingError) {
      console.error('Error logging failed:', loggingError);
    }
  };

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="chat-error-boundary flex flex-col items-center justify-center p-6 bg-white rounded-lg shadow-lg border border-red-200">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Chat Temporarily Unavailable
          </h3>
          
          <p className="text-sm text-gray-600 text-center mb-4 max-w-sm">
            We're experiencing technical difficulties with the chat service. 
            Please try again in a moment.
          </p>
          
          <div className="flex gap-2">
            <button
              onClick={this.handleRetry}
              className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-md 
                         hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 
                         focus:ring-offset-2 transition-colors duration-200"
            >
              Try Again
            </button>
            
            <button
              onClick={this.handleReload}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md 
                         hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 
                         focus:ring-offset-2 transition-colors duration-200"
            >
              Refresh Page
            </button>
          </div>
          
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details className="mt-4 text-xs text-gray-500">
              <summary className="cursor-pointer">Error Details (Dev Mode)</summary>
              <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-auto max-w-sm">
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook-based error boundary wrapper
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) => {
  const WrappedComponent = (props: P) => (
    <ChatBotErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ChatBotErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};